import { createServerFn } from "@tanstack/react-start";

const PLACE_ID = "ChIJFQkCHXhllR4Ri3Sk02mjxTI";
const GBP_LOCATION = "accounts/116827247946865426087/locations/10505468895878169208";
const MAPS_URI = "https://maps.google.com/maps?cid=3658509947226059915";
const GATEWAY = "https://connector-gateway.lovable.dev/google_maps";

export type GoogleReview = {
  id: string;
  author: string;
  authorUri: string;
  photo: string;
  rating: number;
  relativeTime: string;
  publishTime: string;
  text: string;
};

export type GoogleReviewsPayload = {
  rating: number;
  totalRatings: number;
  placeUri: string;
  displayName: string;
  reviews: GoogleReview[];
  fetchedAt: string;
};

type CacheEntry = { at: number; data: GoogleReviewsPayload };
let cache: CacheEntry | null = null;
const TTL_MS = 60 * 60 * 1000; // 1 hour

export const getGoogleReviews = createServerFn({ method: "GET" }).handler(async (): Promise<GoogleReviewsPayload> => {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.data;

  const lovableKey = process.env.LOVABLE_API_KEY;
  const gbpKey = process.env.GOOGLE_BUSINESS_PROFILE_API_KEY;
  // Prefer the full review feed from the practice's own Business Profile; fall back to public Maps data.
  if (lovableKey && gbpKey) {
    try {
      const r = await fetch(`https://connector-gateway.lovable.dev/google_business_profile/my_business/v4/${GBP_LOCATION}/reviews?pageSize=20`, {
        headers: { Authorization: `Bearer ${lovableKey}`, "X-Connection-Api-Key": gbpKey },
      });
      if (!r.ok) throw new Error(`Business Profile [${r.status}]: ${await r.text()}`);
      const j: any = await r.json();
      const stars: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
      const data: GoogleReviewsPayload = {
        rating: Number(j.averageRating ?? 0),
        totalRatings: Number(j.totalReviewCount ?? 0),
        placeUri: MAPS_URI,
        displayName: "N S Physiotherapy",
        reviews: (j.reviews ?? []).filter((x: any) => x.comment).map((x: any) => ({
          id: x.reviewId,
          author: x.reviewer?.displayName ?? "Google user",
          authorUri: "",
          photo: x.reviewer?.profilePhotoUrl ?? "",
          rating: stars[x.starRating] ?? 5,
          relativeTime: new Date(x.createTime).toLocaleDateString("en-ZA", { month: "long", year: "numeric" }),
          publishTime: x.createTime ?? "",
          text: String(x.comment).replace(/\(Translated by Google\)[\s\S]*$/, "").trim(),
        })),
        fetchedAt: new Date().toISOString(),
      };
      cache = { at: Date.now(), data };
      return data;
    } catch (e) {
      console.error("Business Profile reviews failed, falling back to Maps", e);
    }
  }
  const mapsKey = process.env.GOOGLE_MAPS_API_KEY;
  if (!lovableKey || !mapsKey) throw new Error("Google Maps connector not configured");

  const res = await fetch(`${GATEWAY}/places/v1/places/${PLACE_ID}?languageCode=en`, {
    headers: {
      Authorization: `Bearer ${lovableKey}`,
      "X-Connection-Api-Key": mapsKey,
      "X-Goog-FieldMask": "id,displayName,rating,userRatingCount,googleMapsUri,reviews",
    },
  });

  if (!res.ok) {
    const body = await res.text();
    console.error(`Google Places request failed [${res.status}]: ${body}`);
    throw new Error(`Google Places request failed [${res.status}]`);
  }

  const json: any = await res.json();
  const reviews: GoogleReview[] = (json.reviews ?? []).map((r: any) => ({
    id: r.name,
    author: r.authorAttribution?.displayName ?? "Google user",
    authorUri: r.authorAttribution?.uri ?? "",
    photo: r.authorAttribution?.photoUri ?? "",
    rating: r.rating ?? 5,
    relativeTime: r.relativePublishTimeDescription ?? "",
    publishTime: r.publishTime ?? "",
    text: r.text?.text ?? r.originalText?.text ?? "",
  }));

  const data: GoogleReviewsPayload = {
    rating: json.rating ?? 0,
    totalRatings: json.userRatingCount ?? 0,
    placeUri: json.googleMapsUri ?? "",
    displayName: json.displayName?.text ?? "N S Physiotherapy",
    reviews,
    fetchedAt: new Date().toISOString(),
  };

  cache = { at: Date.now(), data };
  return data;
});
